import React, { useEffect, useMemo, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Formik, Form, useFormikContext } from 'formik';

import { useCardsFromWallet } from '../../../hooks/useCardsFromWallet';

import { RegisterCardDialog } from './RegisterCardDialog';
import {
    ProductAmountInput,
    ProductAmountInputFormData as ProductAmountFormData,
} from './ProductAmountInput';
import { CartProductTabPanelProps } from './types';
import { sortOptionValuesAscending } from './utils';

import { createStyle, createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { GiftCard, isCardReloadable, Card, EGiftCard } from 'common/api/e-comm/models/Card';
import { isDefined } from 'common/utils';
import {
    Modal,
    ModalPrimaryBtn,
    ModalSecondaryBtn,
    ModalFooter,
    ModalSize,
} from 'components/modal/Modal';
import { FormikComboField } from './utils/fields';
import { Product } from 'common/api/e-comm/models/Product';
import { useCardBalance } from 'common/hooks/useCardBalance';
import { InMyWallet } from './InMyWallet';
import { useHistory } from 'react-router';
import { Routes } from 'routes';
import { FormBtn } from 'components/form-btn';
import { waitPromise } from 'utils/promiseHelpers';
import { hasErrorWithStatus } from 'common/api/utils/hasErrorWithStatus';
import { getCardBalance } from 'common/api/wallet/cards';
import { StatusCode } from 'common/api/config';
import { useAlert } from 'modules/alerts';
import { selectReloadProducts } from 'common/features/store/duck/brands/duck';
import { useRootSelector } from 'common/features/featuresReducer';
import _ from 'lodash';
import { LinkBtn } from 'components/link-btn';
import { APIError } from 'common/api/models';
import { SmallWarningTriangle } from 'features/wallet/components/CardBalance';

export interface ProductDetailsPanelProps extends CartProductTabPanelProps {
    value: string;
    brandId: number;
    brandName: string;
}

const styles = createStyles({
    offer: 'text-2xl px-0 lg:px-32 py-12 lg:pt-24 text-brand-dark font-bold',
    fieldContainer: ['w-full', { maxWidth: '14rem' }],
    container: [
        'block lg:grid mt-4',
        {
            gridTemplateColumns: 'auto auto',
        },
    ],
    ctrlIncrement: [
        'inline-block',
        {
            maxWidth: '140px',
        },
    ],
    incrementAndTotal: [
        'grid mt-4 lg:mt-0',
        {
            gridTemplateColumns: 'auto auto',
        },
    ],
    listShipToHome: [
        'mb-8 mt-2 text-xs text-grey-1',
        {
            listStyleType: 'disc',
            paddingLeft: '20px',
        },
    ],
    listComboContainer: ['w-full', { maxWidth: '220px' }],
    row: ['grid gap-4 mt-5'],
    registerCard: ['sm:grid gap-4', { gridTemplateColumns: 'minmax(8rem, 14rem) auto' }],
    contentWrapper: createStyle('flex flex-col', {
        minHeight: '320px',
    }),
});

export type ReloadableProductAmountFormData = ProductAmountFormData & { cardId: string };

const getLastFourDigits = (card: GiftCard) => {
    return card.formattedCardNumber ? card.formattedCardNumber.split(' ').pop() : '';
};

const getCardDisplayName = (card: GiftCard) => {
    return card.name ? `${card.name} (${getLastFourDigits(card)})` : card.formattedCardNumber || '';
};

export interface AmountInputProps {
    product: Product;
    cards: Card[];
    onCardBalanceLoading: (loading: boolean) => void;
    onCardBalanceError?: (error: APIError<null> | null) => void;
}

const AmountInput = ({
    product,
    cards,
    onCardBalanceError,
    onCardBalanceLoading,
}: AmountInputProps) => {
    const { values } = useFormikContext<ReloadableProductAmountFormData>();
    const cardId = values.cardId;
    const card = cards.find(({ id }: Card) => id === cardId);
    const { data: balance, error: balanceError, isLoading: balanceLoading } = useCardBalance(card);

    useEffect(() => {
        if (onCardBalanceLoading) {
            onCardBalanceLoading(balanceLoading);
        }
    }, [balanceLoading]);

    // call onCardBalanceError handler with balance error
    useEffect(() => {
        if (onCardBalanceError) {
            if (balanceLoading) {
                onCardBalanceError(null);
                return;
            }

            onCardBalanceError(balanceError);
        }
    }, [onCardBalanceError, balanceLoading, balanceError]);

    const cardBalance =
        card && card.settings?.canCheckBalance ? balance?.cardBalance : card && card?.cardBalance;
    return (
        <ProductAmountInput
            product={product}
            label={<span className="text-brand font-bold">Reload amount</span>}
            hideQuantityField={true}
            cardBalance={cardBalance || 0}
        />
    );
};

const canReloadCard = async (cardId?: string) => {
    if (!cardId) {
        return false;
    }

    const resp = await getCardBalance(cardId);
    return !(
        hasErrorWithStatus(resp, StatusCode.Gone) || hasErrorWithStatus(resp, StatusCode.NotFound)
    );
};

export const ReloadTabPanel = ({
    value,
    brandId,
    brandName,
    addToCart,
    product,
    canCheckout,
}: ProductDetailsPanelProps) => {
    const [showRegisterCard, setShowRegisterCard] = useState(false);

    const history = useHistory();
    const alert = useAlert();

    const reloadProducts = _.values(useRootSelector(selectReloadProducts));
    const walletCards = useCardsFromWallet();

    const {
        cards,
        relatedReloadableProducts,
        hasReloadableCards,
        reloadableCardsList,
        reloadableCards,
    } = useMemo(() => {
        const filteredCards = walletCards.filter((c) => c.brandId === brandId);
        /**
         * This related brands logic is somewhat complex.
         * We need to display any brands on the reload tab of a PDP that share the same supplierCode,
         * and allow users to purchase those other brands from the currently viewed PDP.
         * See test cases in ReloadTabPanel.test.tsx for additional context.
         */

        // fetch all products that share the SAME supplier code as the currently viewed product
        // but have a DIFFERENT brandID (to avoid duplicates) from our reloadProductsById portion of redux.
        const relatedReloadableProducts = reloadProducts.filter(
            (p) => p?.supplierCode === product.supplierCode && p?.brandId !== product.brandId
        );

        // fetch all cards in user's wallet that match a brand from the relatedReloadableProducts list.
        const relatedCards = walletCards.filter((c) =>
            relatedReloadableProducts.find(
                (reloadableProduct) => reloadableProduct?.brandId === c?.brandId
            )
        );

        // add any found related cards to the list of reloadable cards displayed on the PDP.
        filteredCards.push(...relatedCards);
        // end related brands logic

        const reloadableCards = filteredCards.filter(isDefined).filter(isCardReloadable) || [];

        const hasReloadableCards = reloadableCards.length > 0;
        const reloadableCardsList = reloadableCards.map((card) => ({
            value: card.id,
            text: getCardDisplayName(card),
        }));

        return {
            cards: filteredCards,
            relatedReloadableProducts,
            hasReloadableCards,
            reloadableCardsList,
            reloadableCards,
        };
    }, [walletCards, reloadProducts, product, brandId]);

    const goToCardPage = (cardId: string) =>
        history.push(Routes.WalletMyCardDetails.replace(':cardId', cardId));

    const [addingToCart, setAddingToCart] = useState(false);

    const onSubmit = async (formData: ProductAmountFormData) => {
        const canReload = await canReloadCard(formData.cardId);

        /**
         * due to the above related reloads logic, we might be adding a product
         * to a cart that is different from the product displayed on the PDP
         * for example reloading a safeway card from the albertsons PDP since they are
         * related brands.
         */

        // find card id of reload being purchased
        const reloadCard = cards.find((c) => c.id === formData.cardId);

        // find product of card being reloaded
        const cardProduct = relatedReloadableProducts.find(
            (p) => p?.brandId === reloadCard?.brandId
        );

        // cardProduct is defined, that means we're attempting to reload a card that is DIFFERENT
        // from the currently displayed PDP. Otherwise we're just reloading the actual product
        // being displayed.
        const reloadProduct = cardProduct ? cardProduct : product;
        if (reloadProduct && canCheckout && canReload) {
            waitPromise(addToCart(reloadProduct, formData), setAddingToCart);
        }

        // CASE: We cannot reload due to reload card not being active
        if (!canReload) {
            // NOTE: show a modal telling user they cannot add to this reload card
            alert({
                title: 'Inactive Reload',
                message: 'This reload card is inactive and cannot be added to cart.',
                showCloseButton: true,
            });
        }
    };

    const initialValues: ReloadableProductAmountFormData = {
        cardId: hasReloadableCards ? reloadableCardsList[0].value : '',
        selectedOptions: Object.fromEntries(
            product.options.map((opt) => [opt.id, sortOptionValuesAscending(opt.values)[0].value])
        ),
        priceOverride: product.minValue || 0,
        quantity: 1,
    };

    return (
        <Tabs.Panel value={value}>
            <div className={styles.contentWrapper}>
                {product && (
                    <div className="mb-0 sm:mb-16">
                        <div className="text-grey-darkest">
                            Add funds to physical gift cards you’ve already purchased.
                        </div>
                        <Modal
                            title="Register Card"
                            isOpen={showRegisterCard}
                            onClose={() => setShowRegisterCard(false)}
                            size={[ModalSize.CONTENT_WIDTH, ModalSize.CONTENT_WIDTH]}
                        >
                            <RegisterCardDialog
                                brandId={brandId}
                                supplierCode={product.supplierCode}
                            >
                                <ModalFooter className="o:mt-10">
                                    <ModalPrimaryBtn type="submit">
                                        Register This Card
                                    </ModalPrimaryBtn>
                                    <ModalSecondaryBtn
                                        type="button"
                                        onClick={() => setShowRegisterCard(false)}
                                    >
                                        Close
                                    </ModalSecondaryBtn>
                                </ModalFooter>
                            </RegisterCardDialog>
                        </Modal>
                        {!hasReloadableCards && (
                            <div className={styles.offer}>
                                Register a card so you can reload funds when needed.
                                <div className="text-center mt-14">
                                    <Btn
                                        className="w-full md:w-max"
                                        onClick={() => setShowRegisterCard(true)}
                                    >
                                        Register a Card
                                    </Btn>
                                </div>
                            </div>
                        )}
                        {hasReloadableCards && (
                            <Formik
                                initialValues={initialValues}
                                onSubmit={onSubmit}
                                key={product.id}
                            >
                                <ReloadTabPanelForm
                                    brandName={brandName}
                                    cards={cards}
                                    product={product}
                                    reloadableCardsList={reloadableCardsList}
                                    setShowRegisterCard={setShowRegisterCard}
                                    addingToCart={addingToCart}
                                    canCheckout={canCheckout}
                                    reloadableProducts={relatedReloadableProducts}
                                />
                            </Formik>
                        )}
                    </div>
                )}
                <InMyWallet
                    brandName={brandName}
                    items={reloadableCards}
                    onClick={(card) => goToCardPage(card.id)}
                />
            </div>
        </Tabs.Panel>
    );
};

interface ReloadTabPanelFormProps {
    cards: (GiftCard | EGiftCard)[];
    product: Product;
    reloadableCardsList: {
        value: string;
        text: string;
    }[];
    setShowRegisterCard: Function;
    addingToCart: boolean;
    canCheckout: boolean;
    reloadableProducts: (Product | undefined)[];
    brandName: string;
}

const ReloadTabPanelForm = ({
    cards,
    product,
    reloadableCardsList,
    setShowRegisterCard,
    addingToCart,
    canCheckout,
    reloadableProducts,
    brandName,
}: ReloadTabPanelFormProps) => {
    const [reloadProduct, setReloadProduct] = useState<Product>(product);
    const formCtx = useFormikContext<ProductAmountFormData>();
    const history = useHistory();

    useEffect(() => {
        // Because we are displaying multiple different brands on this PDP due to related reloads
        // we also need to make sure we're adjusting the reload min/max amounts to reflect
        // the brand of the selected reload card.
        const selectedCard = cards.find((c) => c?.id === formCtx?.values?.cardId);
        const selectedProduct = reloadableProducts.find(
            (p) => p?.brandId === selectedCard?.brandId
        );

        if (selectedProduct) {
            setReloadProduct(selectedProduct);
        }
    }, [formCtx, cards, reloadableProducts]);

    const onEditCardDetails = () => {
        // redirect to wallet page
        if (!formCtx.values.cardId) {
            return;
        }

        history.push(Routes.WalletMyCardDetails.replace(':cardId', formCtx.values.cardId));
    };

    const [selectedCardError, setSelectedCardError] = useState<APIError<null> | null>();
    const [selectedCardLoading, setSelectedCardLoading] = useState(false);

    return (
        <Form>
            <div
                className="mt-4"
                data-cnstrc-product-detail
                data-cnstrc-item-id={product.brandId}
                data-cnstrc-item-name={brandName}
                data-cnstrc-item-price={product.unitPrice}
            >
                <div className={styles.row}>
                    <div className={styles.registerCard}>
                        <div className="mb-6 sm:mb-0 max-w-xs">
                            <FormikComboField
                                name="cardId"
                                label={
                                    <span className="text-brand font-bold">
                                        Your registered card
                                    </span>
                                }
                                options={reloadableCardsList}
                            />
                            <a
                                href="#"
                                className="text-xs no-underline"
                                onClick={(e) => {
                                    setShowRegisterCard(true);
                                    e.preventDefault();
                                }}
                            >
                                + Register another card
                            </a>
                            {selectedCardError ? (
                                <div className="text-sm text-error">
                                    <SmallWarningTriangle /> This card's PIN is missing or
                                    incorrect. To view your current balance or add money to this
                                    card,{' '}
                                    <LinkBtn onClick={onEditCardDetails}>
                                        Edit Card Details.
                                    </LinkBtn>
                                </div>
                            ) : null}
                        </div>
                        <AmountInput
                            cards={cards}
                            product={reloadProduct}
                            onCardBalanceError={setSelectedCardError}
                            onCardBalanceLoading={setSelectedCardLoading}
                        />
                    </div>
                </div>
                <div className="text-center sm:text-right mt-8">
                    <FormBtn
                        type="submit"
                        className="pdp_add_to_cart_button w-80 md:w-48 inline-block"
                        isSubmitting={addingToCart}
                        disabled={!canCheckout || selectedCardLoading || Boolean(selectedCardError)}
                    >
                        Add to Cart
                    </FormBtn>
                    {product.isOvernightReload && (
                        <p className="text-brand font-semibold text-xs mt-4">
                            Funds will be available the next business day.
                        </p>
                    )}
                </div>
            </div>
        </Form>
    );
};
